/**
 * Volatile keys monday ships on almost every populated `value` and ignores on write
 * `[cvs§4.2]`. They are dropped by `parseValue`, preserved by `fromMondayValue`, and never
 * compared by `isEqual`.
 */
export const VOLATILE_VALUE_KEYS = ['changed_at', 'updated_at'];
export function isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
/** A copy without the volatile keys; non-objects pass through. */
export function withoutVolatileKeys(value) {
    if (!isPlainObject(value))
        return value;
    const copy = { ...value };
    for (const key of VOLATILE_VALUE_KEYS)
        delete copy[key];
    return copy;
}
/** Order-insensitive structural equality (object key order is not semantic in monday JSON). */
export function deepEqual(a, b) {
    if (a === b)
        return true;
    if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object')
        return false;
    if (Array.isArray(a) !== Array.isArray(b))
        return false;
    if (Array.isArray(a) && Array.isArray(b)) {
        return a.length === b.length && a.every((entry, index) => deepEqual(entry, b[index]));
    }
    const left = a;
    const right = b;
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length)
        return false;
    return leftKeys.every((key) => Object.prototype.hasOwnProperty.call(right, key) && deepEqual(left[key], right[key]));
}
/** The default `isEqual` of M1-B5 §5.1: structural equality ignoring the volatile keys. */
export function defaultIsEqual(a, b) {
    if (a === null || a === undefined)
        return b === null || b === undefined;
    if (b === null || b === undefined)
        return false;
    return deepEqual(withoutVolatileKeys(a), withoutVolatileKeys(b));
}
export function emptySortKeys() {
    return { text: null, number: null, date: null };
}
/** `''` is never written into `text_value`: an empty shadow is null, so `is_empty` is one test. */
export function textShadow(text) {
    return text === undefined || text === null || text === '' ? null : text;
}
const FORMATTERS = new Map();
function formatterFor(timezone) {
    let formatter = FORMATTERS.get(timezone);
    if (formatter === undefined) {
        formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        FORMATTERS.set(timezone, formatter);
    }
    return formatter;
}
/**
 * The wall clock an instant shows in `timezone` (default UTC). monday converts the time in
 * `ColumnValue.text` to the caller's timezone while `value` stays UTC `[09§A.7 date]`.
 */
export function wallClockIn(instant, timezone) {
    if (timezone === undefined || timezone === 'UTC') {
        return {
            year: instant.getUTCFullYear(),
            month: instant.getUTCMonth() + 1,
            day: instant.getUTCDate(),
            hour: instant.getUTCHours(),
            minute: instant.getUTCMinutes(),
            second: instant.getUTCSeconds(),
        };
    }
    const parts = formatterFor(timezone).formatToParts(instant);
    const read = (type) => {
        const part = parts.find((entry) => entry.type === type);
        return part === undefined ? 0 : Number(part.value);
    };
    return {
        year: read('year'),
        month: read('month'),
        day: read('day'),
        hour: read('hour'),
        minute: read('minute'),
        second: read('second'),
    };
}
export function pad2(value) {
    return String(value).padStart(2, '0');
}
export function formatDatePart(clock) {
    return `${String(clock.year).padStart(4, '0')}-${pad2(clock.month)}-${pad2(clock.day)}`;
}
export function formatTimeHm(clock) {
    return `${pad2(clock.hour)}:${pad2(clock.minute)}`;
}
export function formatTimeHms(clock) {
    return `${formatTimeHm(clock)}:${pad2(clock.second)}`;
}
/**
 * monday's `text` for `creation_log` / `last_updated`: `"YYYY-MM-DD HH:MM:SS UTC"` — always the
 * literal suffix `UTC`, never localised `[cvs§2 last_updated]`.
 */
export function formatUtcStamp(instant) {
    const clock = wallClockIn(instant);
    return `${formatDatePart(clock)} ${formatTimeHms(clock)} UTC`;
}
/** Parses monday's ISO timestamps (`2025-02-10T17:01:07Z`); `null` when unparseable. */
export function parseInstant(raw) {
    if (raw instanceof Date)
        return Number.isNaN(raw.getTime()) ? null : raw;
    if (typeof raw !== 'string' || raw.trim() === '')
        return null;
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}
/** monday serialises system timestamps without milliseconds: `2025-02-10T17:01:07Z`. */
export function toIsoSeconds(instant) {
    return `${instant.toISOString().slice(0, 19)}Z`;
}
/**
 * monday's `updated_at` inside a typed `ColumnValue` fragment — **not** the raw `changed_at`
 * the value carries.
 *
 * Measured on the 2026-09-10 snapshot: a `timeline` cell whose `value.changed_at` is
 * `"2025-10-20T11:54:30.882Z"` comes back as `updated_at: "2025-10-20T11:54:30+00:00"`, and
 * the same reformat holds for `location`, `link`, `email`, `phone`, `rating`, `hour`,
 * `country`, `world_clock` and `checkbox`: **seconds precision, `+00:00` offset, no `Z`**.
 * An unparseable or absent stamp stays null.
 */
export function toFragmentTimestamp(raw) {
    const instant = parseInstant(raw ?? null);
    if (instant === null)
        return null;
    return `${instant.toISOString().slice(0, 19)}+00:00`;
}
//# sourceMappingURL=helpers.js.map